import React, { useEffect, useState } from "react";
import api from "../context/api";
import "../styles/AdminTransactions.css";

const AdminTransactions = () => {
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("success");
  const [typeTab, setTypeTab] = useState("deposit");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const [depositResponse, withdrawalResponse] = await Promise.allSettled([
        api.get("/getPayment?type=deposit&limit=10000"),
        api.get("/getPayment?type=withdrawal&limit=10000"),
      ]);

      if (depositResponse.status === "fulfilled") {
        setDeposits(depositResponse.value.data?.transactions || []);
      } else {
        console.error("Failed to fetch deposits:", depositResponse.reason?.message);
        setDeposits([]);
      }

      if (withdrawalResponse.status === "fulfilled") {
        setWithdrawals(withdrawalResponse.value.data?.transactions || []);
      } else {
        console.error("Failed to fetch withdrawals:", withdrawalResponse.reason?.message);
        setWithdrawals([]);
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error.message);
      setDeposits([]);
      setWithdrawals([]);
    } finally {
      setLoading(false);
    }
  };

  const isWithinDateRange = (createdAt) => {
    if (!createdAt) return false;

    const transactionDate = new Date(createdAt);
    if (Number.isNaN(transactionDate.getTime())) return false;

    if (!fromDate && !toDate) return true;

    const transactionTime = transactionDate.getTime();

    if (fromDate) {
      const fromTime = new Date(`${fromDate}T00:00:00`).getTime();
      if (transactionTime < fromTime) return false;
    }

    if (toDate) {
      const toTime = new Date(`${toDate}T23:59:59.999`).getTime();
      if (transactionTime > toTime) return false;
    }

    return true;
  };

  const filteredDeposits = deposits.filter(
    (item) => (item.status || "").toLowerCase() === statusFilter && isWithinDateRange(item.createdAt)
  );

  const filteredWithdrawals = withdrawals.filter(
    (item) => (item.status || "").toLowerCase() === statusFilter && isWithinDateRange(item.createdAt)
  );

  const totalDeposits = deposits
    .filter((item) => (item.status || "").toLowerCase() === "success" && isWithinDateRange(item.createdAt))
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const totalWithdrawals = withdrawals
    .filter((item) => (item.status || "").toLowerCase() === "success" && isWithinDateRange(item.createdAt))
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);

  const netFlow = totalDeposits - totalWithdrawals;

  const getDisplayName = (transaction) => {
    return (
      transaction?.user?.fullName ||
      transaction?.fullName ||
      transaction?.name ||
      "N/A"
    );
  };

  const formatAmount = (amount) => `GHC ${Number(amount || 0).toLocaleString()}`;

  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return "N/A";
    return parsedDate.toLocaleString();
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const statusLabels = {
    success: "successful",
    pending: "pending",
    failed: "failed",
  };

  const renderBreakdownTable = (transactions) => (
    <div className="admin-transactions-card">
      <div className="admin-transactions-section-header">
        <div className="admin-transactions-section-tabs">
          <button
            type="button"
            className={`admin-tab-btn ${typeTab === "deposit" ? "active" : ""}`}
            onClick={() => setTypeTab("deposit")}
          >
            Deposit
          </button>
          <button
            type="button"
            className={`admin-tab-btn ${typeTab === "withdrawal" ? "active" : ""}`}
            onClick={() => setTypeTab("withdrawal")}
          >
            Withdrawal
          </button>
        </div>
        <p>
          {transactions.length} {statusLabels[statusFilter]} {typeTab} transaction(s)
        </p>
      </div>

      {loading ? (
        <div className="admin-transactions-empty">Loading transactions...</div>
      ) : transactions.length === 0 ? (
        <div className="admin-transactions-empty">No {statusLabels[statusFilter]} {typeTab} transactions found.</div>
      ) : (
        <div className="admin-transactions-table-wrap">
          <table className="admin-transactions-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone Number</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => (
                <tr key={transaction.id || transaction.reference}>
                  <td>{getDisplayName(transaction)}</td>
                  <td>{transaction.phoneNumber || transaction?.user?.phoneNumber || "N/A"}</td>
                  <td className="admin-amount-cell">{formatAmount(transaction.amount)}</td>
                  <td>
                    <span className={`admin-tx-status ${(transaction.status || "pending").toLowerCase()}`}>
                      {transaction.status || "pending"}
                    </span>
                  </td>
                  <td>{formatDate(transaction.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-transactions-page">
      <div className="admin-transactions-header">
        <h1>Wallet Transactions</h1>
      </div>

      <div className="admin-transactions-tabs">
        <button
          type="button"
          className={`admin-tab-btn ${statusFilter === "success" ? "active" : ""}`}
          onClick={() => setStatusFilter("success")}
        >
          Success
        </button>
        <button
          type="button"
          className={`admin-tab-btn ${statusFilter === "pending" ? "active" : ""}`}
          onClick={() => setStatusFilter("pending")}
        >
          Pending
        </button>
        <button
          type="button"
          className={`admin-tab-btn ${statusFilter === "failed" ? "active" : ""}`}
          onClick={() => setStatusFilter("failed")}
        >
          Failed
        </button>
      </div>

      <div className="admin-transactions-date-filters">
        <label className="admin-date-field">
          <span>From</span>
          <input
            type="date"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
          />
        </label>
        <label className="admin-date-field">
          <span>To</span>
          <input
            type="date"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
          />
        </label>
        <button
          type="button"
          className="admin-tab-btn"
          onClick={() => {
            setFromDate("");
            setToDate("");
          }}
          disabled={!fromDate && !toDate}
        >
          Clear Dates
        </button>
        <button
          type="button"
          className="admin-tab-btn"
          onClick={fetchTransactions}
          disabled={loading}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>
   <div className="admin-transactions-summary bottom-summary">
        <div className="summary-item">
          <span className="summary-label">Success Deposits Total (Date Range):</span>
          <span className="summary-value success">GHC {totalDeposits.toLocaleString()}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Success Withdrawals Total (Date Range):</span>
          <span className="summary-value withdrawal">GHC {totalWithdrawals.toLocaleString()}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Net Flow:</span>
          <span className={`summary-value ${netFlow >= 0 ? "success" : "withdrawal"}`}>
            GHC {netFlow.toLocaleString()}
          </span>
        </div>
      </div>
      <div className="admin-transactions-grid">
        {renderBreakdownTable(typeTab === "deposit" ? filteredDeposits : filteredWithdrawals)}
      </div>
    </div>
  );
};

export default AdminTransactions;
